/**
 * The Force sync picker: which of the templates already on this device to re-download.
 *
 * An account can hold hundreds of phone models and a seller uses a few, so the sweep is chosen,
 * not implied. The rows arrive in memory — opening this costs no network call — and each row is
 * one label, so 500 of them stay cheap to render.
 */
import { useMemo, useState } from "react";
import { Modal } from "../ui/Modal.js";

export interface ForceSyncRow {
  templateId: string;
  name: string;
  brand: string;
  model: string;
  code?: string | null;
}

interface Group {
  brand: string;
  rows: ForceSyncRow[];
}

function groupByBrand(rows: ForceSyncRow[]): Group[] {
  const byBrand = new Map<string, ForceSyncRow[]>();
  for (const r of rows) byBrand.set(r.brand, [...(byBrand.get(r.brand) ?? []), r]);
  return [...byBrand.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([brand, rs]) => ({ brand, rows: rs.sort((a, b) => a.model.localeCompare(b.model)) }));
}

function matches(r: ForceSyncRow, q: string): boolean {
  return !q || `${r.brand} ${r.model} ${r.name} ${r.code ?? ""}`.toLowerCase().includes(q);
}

export function ForceSyncPicker({ rows, onConfirm, onClose }: {
  rows: ForceSyncRow[];
  onConfirm: (templateIds: string[]) => void;
  onClose: () => void;
}) {
  const [ticked, setTicked] = useState<ReadonlySet<string>>(() => new Set(rows.map((r) => r.templateId)));
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const matching = useMemo(() => rows.filter((r) => matches(r, q)), [rows, q]);
  const groups = useMemo(() => groupByBrand(matching), [matching]);

  const toggle = (id: string) => setTicked((prev) => {
    const next = new Set(prev);
    if (!next.delete(id)) next.add(id);
    return next;
  });
  const setMatching = (on: boolean) => setTicked((prev) => {
    const next = new Set(prev);
    for (const r of matching) {
      if (on) next.add(r.templateId);
      else next.delete(r.templateId);
    }
    return next;
  });
  const confirm = () => {
    if (ticked.size === 0) return;
    onConfirm(rows.filter((r) => ticked.has(r.templateId)).map((r) => r.templateId));
  };

  return (
    <Modal
      title="Force sync"
      subtitle="Re-download the templates and mock-ups already on this device."
      onClose={onClose}
      testid="force-sync-picker"
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn--primary" onClick={confirm}
                  disabled={ticked.size === 0} data-testid="force-sync-confirm">
            Force sync {ticked.size}
          </button>
        </>
      }
    >
      <div className="force-sync__tools">
        <input className="input" type="search" placeholder="Search brand, model or code"
               aria-label="Search templates" value={query}
               onChange={(e) => setQuery(e.target.value)} data-testid="force-sync-search" />
        <button type="button" className="btn btn--sm btn--ghost" onClick={() => setMatching(true)}
                data-testid="force-sync-all">Select all</button>
        <button type="button" className="btn btn--sm btn--ghost" onClick={() => setMatching(false)}
                data-testid="force-sync-none">Select none</button>
        <span className="faint" role="status" data-testid="force-sync-count">
          {ticked.size} of {rows.length} selected
        </span>
      </div>
      {groups.length === 0 && <p className="faint">No template matches “{query}”.</p>}
      {groups.map((g) => (
        <section key={g.brand} className="force-sync__group" data-testid="force-sync-group">
          <h3 className="force-sync__brand">{g.brand}</h3>
          <ul className="force-sync__list">
            {g.rows.map((r) => (
              <li key={r.templateId} data-testid="force-sync-row">
                <label className="force-sync__row">
                  <input type="checkbox" checked={ticked.has(r.templateId)}
                         aria-label={`${r.model} — ${r.name}`}
                         onChange={() => toggle(r.templateId)} />
                  <span className="force-sync__model">{r.model}</span>
                  <span className="faint">{r.code ? `${r.name} · ${r.code}` : r.name}</span>
                </label>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </Modal>
  );
}
